import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Show } from "@clerk/react";
import { Toaster } from "sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { MeProvider, useMe } from "@/components/app/MeContext";
import Layout from "@/components/app/Layout";
import { SignInPage } from "@/pages/SignInPage";
import Dashboard from "@/pages/Dashboard";
import LmRegister from "@/pages/LmRegister";
import OrderNew from "@/pages/OrderNew";
import OrderDetail from "@/pages/OrderDetail";
import Logistics from "@/pages/Logistics";
import DispatchPlanning from "@/pages/DispatchPlanning";
import Invoices from "@/pages/Invoices";
import OtherMasters from "@/pages/OtherMasters";
import PendingPo from "@/pages/PendingPo";
import Reports from "@/pages/Reports";
import Approvals from "@/pages/Approvals";
import Customers from "@/pages/Customers";
import Items from "@/pages/Items";
import DocumentsPage from "@/pages/Documents";
import AssetGroups from "@/pages/AssetGroups";
import AssetGroupDetail from "@/pages/AssetGroupDetail";
import AssetUnits from "@/pages/AssetUnits";
import AssetUnitDetail from "@/pages/AssetUnitDetail";
import AssetImport from "@/pages/AssetImport";
import Designs from "@/pages/Designs";
import DesignDetail from "@/pages/DesignDetail";
import Admin from "@/pages/Admin";
import Quotes from "@/pages/Quotes";
import QuoteDetail, { QuoteNew } from "@/pages/QuoteDetail";
import Proformas from "@/pages/Proformas";
import ProformaDetail from "@/pages/ProformaDetail";
import SalesProspects from "@/pages/SalesProspects";
import SalesTerms from "@/pages/SalesTerms";
import SalesLetterhead from "@/pages/SalesLetterhead";
import SalesTeam from "@/pages/SalesTeam";

const AdminOnly = ({ children }) => {
  const { me } = useMe();
  return me.roles.includes("admin") ? children : <Navigate to="/" replace />;
};

const Protected = () => (
  <>
    <Show when="signed-in">
      <MeProvider>
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<Dashboard />} />
            <Route path="orders" element={<LmRegister />} />
            <Route path="lms" element={<Navigate to="/orders?tab=lms" replace />} />
            <Route path="lmo" element={<Navigate to="/orders?tab=lmo" replace />} />
            <Route path="orders/new" element={<OrderNew />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="dispatches" element={<Navigate to="/logistics?tab=outward" replace />} />
            <Route path="quality-control" element={<DispatchPlanning key="qc" qc />} />
            <Route path="reports" element={<Reports />} />
            <Route path="logistics" element={<Logistics />} />
            <Route path="dispatch-planning" element={<DispatchPlanning />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="reports/pending-po" element={<PendingPo />} />
            <Route path="reports/:cat" element={<Reports />} />
            <Route path="masters/other" element={<OtherMasters />} />
            <Route path="masters/logistics" element={<OtherMasters />} />
            <Route path="approvals" element={<Approvals />} />
            <Route path="masters/customers" element={<Customers />} />
            <Route path="masters/items" element={<Items />} />
            <Route path="masters/parts" element={<Navigate to="/masters/items" replace />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="assets/groups" element={<AssetGroups />} />
            <Route path="assets/groups/:id" element={<AssetGroupDetail />} />
            <Route path="assets/units" element={<AssetUnits />} />
            <Route path="assets/units/:id" element={<AssetUnitDetail />} />
            <Route path="assets/import" element={<AssetImport />} />
            <Route path="designs" element={<Designs />} />
            <Route path="designs/:id" element={<DesignDetail />} />
            <Route path="sales/quotes" element={<Quotes />} />
            <Route path="sales/quotes/new" element={<QuoteNew />} />
            <Route path="sales/quotes/:id" element={<QuoteDetail />} />
            <Route path="sales/proformas" element={<Proformas />} />
            <Route path="sales/proforma/:id" element={<ProformaDetail />} />
            <Route path="sales/prospects" element={<SalesProspects />} />
            <Route path="sales/terms" element={<SalesTerms />} />
            <Route path="sales/team" element={<AdminOnly><SalesTeam /></AdminOnly>} />
            <Route path="sales/letterhead" element={<AdminOnly><SalesLetterhead /></AdminOnly>} />
            <Route path="admin" element={<AdminOnly><Admin /></AdminOnly>} />
            <Route path="admin/:section" element={<AdminOnly><Admin /></AdminOnly>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </MeProvider>
    </Show>
    <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
  </>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ClerkAuthProvider>
          <Routes>
            <Route path="/sign-in/*" element={<SignInPage mode="sign-in" />} />
            <Route path="/sign-up/*" element={<SignInPage mode="sign-up" />} />
            <Route path="/*" element={<Protected />} />
          </Routes>
        </ClerkAuthProvider>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
